/**
 * Phase 21.4 Batch 3G — Import historical Weekly Spotlight winners
 * into the Hall of Champions.
 *
 * Fetches candidates from GET /api/admin/hall-of-champions/import-candidates
 * (source=weekly_spotlight) — every published spotlight_week entry
 * that isn't already mirrored into hall_of_champions. Admin picks a
 * subset and hits "Import selected" → POST /admin/hall-of-champions/import.
 *
 * NO coin grants, NO Stripe writes. Historical bookkeeping only.
 */
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";

const positionEmoji = (p) => (p === 1 ? "🥇" : p === 2 ? "🥈" : p === 3 ? "🥉" : `#${p}`);

export const HoFImportSpotlightModal = ({ open, onClose, onImported }) => {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/hall-of-champions/import-candidates", {
        params: { source: "weekly_spotlight" },
      });
      setCandidates(r.data?.candidates || []);
      setSelected({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const keyOf = (c) => `${c.spotlight_id}|${c.user_id}|${c.position}`;
  const toggle = (c) => {
    if (c.already_imported) return;
    setSelected((s) => ({ ...s, [keyOf(c)]: !s[keyOf(c)] }));
  };
  const selectAll = () => {
    const next = {};
    for (const c of candidates) if (!c.already_imported) next[keyOf(c)] = true;
    setSelected(next);
  };
  const clearAll = () => setSelected({});

  const doImport = async () => {
    const items = candidates.filter((c) => selected[keyOf(c)]);
    if (items.length === 0) {
      toast.error("Pick at least one to import");
      return;
    }
    setImporting(true);
    try {
      const r = await api.post("/admin/hall-of-champions/import", {
        source: "weekly_spotlight",
        items,
      });
      toast.success(`Imported ${r.data.created_count} · Skipped ${r.data.skipped_count}`);
      onImported?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const eligibleCount = candidates.filter((c) => !c.already_imported).length;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      onClick={onClose}
      data-testid="hof-import-spotlight-modal"
    >
      <Card
        className="max-w-3xl w-full max-h-[90vh] overflow-y-auto bg-[#0f0f14] border border-white/10 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xl font-black text-white">📥 Import Weekly Spotlight Winners</h3>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white text-xl leading-none"
            aria-label="Close"
            data-testid="hof-import-close"
          >×</button>
        </div>
        <p className="text-xs text-gray-400 mb-3">
          Pick which past Weekly Spotlight winners should appear in the Hall of Champions. Already-imported rows are shown but locked. <strong className="text-amber-200">No coins</strong>, no Stripe writes.
        </p>

        <div className="flex flex-wrap gap-2 mb-3 items-center">
          <Button
            onClick={selectAll}
            disabled={loading || eligibleCount === 0}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 text-xs h-8"
            data-testid="hof-import-select-all"
          >
            Select all ({eligibleCount})
          </Button>
          <Button
            onClick={clearAll}
            disabled={loading || selectedCount === 0}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 text-xs h-8"
            data-testid="hof-import-clear"
          >
            Clear
          </Button>
          <span className="text-xs text-gray-400 ml-auto" data-testid="hof-import-count">
            {selectedCount} selected · {candidates.length} total
          </span>
        </div>

        {loading && <p className="text-sm text-gray-500 py-6 text-center" data-testid="hof-import-loading">Loading candidates…</p>}

        {!loading && candidates.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center" data-testid="hof-import-empty">
            No past Weekly Spotlight winners found.
          </p>
        )}

        {!loading && candidates.length > 0 && (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="hof-import-candidates">
            {candidates.map((c) => {
              const key = keyOf(c);
              const isSelected = !!selected[key];
              return (
                <button
                  key={key}
                  type="button"
                  disabled={c.already_imported}
                  onClick={() => toggle(c)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                    c.already_imported
                      ? "border-white/5 bg-white/[0.02] opacity-50 cursor-not-allowed"
                      : isSelected
                        ? "border-amber-500/60 bg-amber-500/10"
                        : "border-white/10 bg-black/40 hover:border-purple-500/40"
                  }`}
                  data-testid={`hof-import-candidate-${key}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={c.already_imported}
                    readOnly
                    className="w-4 h-4 accent-amber-400 flex-shrink-0"
                  />
                  <div className="w-12 h-12 rounded-md overflow-hidden bg-gray-800 flex-shrink-0">
                    {c.cover_art ? (
                      <img src={c.cover_art} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xl">🎤</div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm font-black truncate">{c.artist_name || c.user_id}</p>
                    <p className="text-[11px] text-gray-400 truncate">{c.track_title || "—"} · {c.week_label}</p>
                  </div>
                  <span className="text-lg font-black flex-shrink-0" data-testid={`hof-import-position-${key}`}>
                    {positionEmoji(c.position)}
                  </span>
                  {c.already_imported && (
                    <span className="text-[10px] uppercase tracking-widest text-cyan-300/70 font-black flex-shrink-0">
                      imported
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="hof-import-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={doImport}
            disabled={importing || selectedCount === 0}
            className="bg-white text-black hover:bg-amber-200 font-black disabled:opacity-40"
            data-testid="hof-import-submit"
          >
            {importing ? "Importing…" : `Import ${selectedCount} selected`}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default HoFImportSpotlightModal;
